import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  disconnectEfactura,
  getEfacturaMessage,
  getEfacturaStatus,
  listEfacturaCompanies,
  listEfacturaMessages,
  setEfacturaSettled,
  startEfacturaAuthorization,
  syncEfactura,
  type EfacturaFilters,
} from '../lib/efacturaApi';
import { useActiveCompanyId } from '../store/useCompanyStore';

const EFACTURA_KEY = ['efactura'] as const;

/** Connection status for one firm. Runs a real ANAF probe, so it is not refetched aggressively. */
export function useEfacturaStatus(companyId?: string, enabled = true) {
  return useQuery({
    queryKey: [...EFACTURA_KEY, 'status', companyId ?? null],
    queryFn: () => getEfacturaStatus(companyId),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useEfacturaCompanies() {
  return useQuery({
    queryKey: [...EFACTURA_KEY, 'companies'],
    queryFn: listEfacturaCompanies,
    staleTime: 60_000,
  });
}

export function useEfacturaMessages(filters: EfacturaFilters = {}) {
  // Global firm switcher (multi-company): threaded in like the other finance
  // hooks; an explicit filters.companyId wins.
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const merged: EfacturaFilters = { companyId: activeCompanyId, ...filters };
  return useQuery({
    queryKey: [...EFACTURA_KEY, 'messages', merged],
    queryFn: () => listEfacturaMessages(merged),
    placeholderData: (prev) => prev,
  });
}

export function useEfacturaMessage(id: string | null) {
  return useQuery({
    queryKey: [...EFACTURA_KEY, 'message', id],
    queryFn: () => getEfacturaMessage(id as string),
    enabled: Boolean(id),
  });
}

export function useSyncEfactura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyId?: string; zile?: number }) => syncEfactura(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EFACTURA_KEY });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useStartEfacturaAuthorization() {
  return useMutation({
    mutationFn: (companyId: string) => startEfacturaAuthorization(companyId),
  });
}

export function useDisconnectEfactura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => disconnectEfactura(companyId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EFACTURA_KEY });
      void qc.invalidateQueries({ queryKey: ['settings', 'integration', 'anafEfactura'] });
    },
  });
}

/** Toggle achitată / încasată on one SPV message; the lists refetch. */
export function useSetEfacturaSettled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; settled: boolean }) =>
      setEfacturaSettled(input.id, input.settled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...EFACTURA_KEY, 'messages'] });
      void qc.invalidateQueries({ queryKey: [...EFACTURA_KEY, 'message'] });
      // The mark changes receivables/payables on the dashboard and the mirrored
      // row in Facturi; both cache for minutes otherwise.
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
